import React from "react";
import { Loader2 } from "lucide-react";
import { toast } from "react-toastify";

import {
  createStreamSetting,
  updateStreamSetting,
} from "../../../../api/retensionApis";
import { useCourtSessionOptions } from "../../../../hooks/useCourtSessionOptions";
import type { SearchData } from "../../../../types";
import type {
  StreamSettingPayload,
  StreamSettingSelectionLabels,
} from "./StreamSetting.types";
import StreamCourtSelect from "./StreamCourtSelect";
import StreamDelaySelect from "./StreamDelaySelect";

interface DistrictStreamSettingsFormProps {
  children?: React.ReactNode;
  editId: string | null;
  formValues: StreamSettingPayload;
  onCancel: () => void;
  onFormValuesChange: (values: StreamSettingPayload) => void;
  onSaved: () => void;
  selectedLabels?: StreamSettingSelectionLabels;
}

const getSaveLabel = (isSaving: boolean, editId: string | null) => {
  if (isSaving) return editId ? "Updating..." : "Saving...";

  return editId ? "Update Streaming" : "Save Streaming";
};

const DistrictStreamSettingsForm: React.FC<DistrictStreamSettingsFormProps> = ({
  children,
  editId,
  formValues,
  onCancel,
  onFormValuesChange,
  onSaved,
  selectedLabels = {},
}) => {
  const [isSaving, setIsSaving] = React.useState(false);
  const {
    states,
    districts,
    complexes,
    establishments,
    courtRooms,
    courtEstablishment,
    controls,
  } = useCourtSessionOptions(
    toCourtSearchData({
      state: formValues.stateId,
      district: formValues.districtId,
      complex: formValues.complexId,
      establishment: formValues.establishmentId || "",
      courtRoom: formValues.courtRoomId || "",
    }),
    "district",
  );

  const setDelay = (delayInMinutes: number | string) => {
    onFormValuesChange({
      ...formValues,
      delayInMinutes,
    });
  };

  const handleSave = async () => {
    if (!formValues.stateId) {
      toast.error("Select state");
      return;
    }

    if (!formValues.districtId) {
      toast.error("Select district");
      return;
    }

    if (!formValues.complexId) {
      toast.error("Select court complex");
      return;
    }

    if (courtEstablishment && !formValues.establishmentId) {
      toast.error("Select establishment");
      return;
    }

    if (!formValues.courtRoomId) {
      toast.error("Select court room");
      return;
    }

    const delay = Number(formValues.delayInMinutes);

    if (Number.isNaN(delay)) {
      toast.error("Delay must be a number");
      return;
    }

    try {
      setIsSaving(true);

      const payload = {
        courtRoomId: formValues.courtRoomId,
        delayInMinutes: delay,
        links: formValues.links || [],
        courtRoomModel: "CourtRoom",
      };

      if (editId) {
        await updateStreamSetting({ id: editId, ...payload });
        toast.success("District stream updated successfully");
      } else {
        await createStreamSetting(payload);
        toast.success("District stream saved successfully");
      }

      onSaved();
    } catch (error: any) {
      toast.error(
        error?.response?.data?.message ||
          error?.message ||
          "Something went wrong",
      );
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <>
      <div className="p-6 space-y-8 min-h-[45vh]">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          <StreamCourtSelect
            label="State"
            placeholder="Select State"
            searchPlaceholder="Search state..."
            options={states}
            value={formValues.stateId}
            selectedLabel={selectedLabels.state}
            onChange={(stateId) => {
              onFormValuesChange({
                ...formValues,
                highcourtId: "",
                benchId: "",
                benchRoomId: "",
                courtRoomId: "",
                stateId,
                districtId: "",
                complexId: "",
                establishmentId: "",
              });
            }}
          />

          <StreamCourtSelect
            label="District"
            placeholder="Select District"
            searchPlaceholder="Search district..."
            options={districts}
            value={formValues.districtId}
            disabled={!formValues.stateId}
            paginationControl={controls.districts}
            selectedLabel={selectedLabels.district}
            onChange={(districtId) => {
              onFormValuesChange({
                ...formValues,
                districtId,
                complexId: "",
                establishmentId: "",
                courtRoomId: "",
              });
            }}
          />

          <StreamCourtSelect
            label="Court Complex"
            placeholder="Select Court Complex"
            searchPlaceholder="Search court complex..."
            options={complexes}
            value={formValues.complexId}
            disabled={!formValues.districtId}
            paginationControl={controls.complexes}
            selectedLabel={selectedLabels.complex}
            onChange={(complexId) => {
              onFormValuesChange({
                ...formValues,
                complexId,
                establishmentId: "",
                courtRoomId: "",
              });
            }}
          />

          {courtEstablishment && (
            <StreamCourtSelect
              label="Establishment"
              placeholder="Select Establishment"
              searchPlaceholder="Search establishment..."
              options={establishments}
              value={formValues.establishmentId || ""}
              disabled={!formValues.complexId}
              paginationControl={controls.establishments}
              selectedLabel={selectedLabels.establishment}
              onChange={(establishmentId) => {
                onFormValuesChange({
                  ...formValues,
                  establishmentId,
                  courtRoomId: "",
                });
              }}
            />
          )}

          <StreamCourtSelect
            label="Court Room"
            placeholder="Select Court Room"
            searchPlaceholder="Search court room..."
            options={courtRooms}
            value={formValues.courtRoomId || ""}
            disabled={
              !formValues.complexId ||
              (Boolean(courtEstablishment) && !formValues.establishmentId)
            }
            paginationControl={controls.courtRooms}
            selectedLabel={selectedLabels.courtRoom}
            onChange={(courtRoomId) => {
              onFormValuesChange({
                ...formValues,
                courtRoomId,
              });
            }}
          />

          <StreamDelaySelect
            value={formValues.delayInMinutes}
            onChange={setDelay}
          />
        </div>

        {children}
      </div>

      <div className="px-6 py-4 flex justify-end gap-3">
        <button
          onClick={onCancel}
          className="px-5 py-2 border rounded-lg hover:bg-gray-50"
        >
          Cancel
        </button>

        <button
          onClick={handleSave}
          disabled={isSaving}
          className={`min-w-32 px-6 py-2 flex items-center justify-center gap-2 rounded-lg text-white transition ${
            isSaving
              ? "bg-blue-400 opacity-70 cursor-not-allowed"
              : "bg-blue-600 hover:bg-blue-700"
          }`}
        >
          {isSaving && <Loader2 className="h-4 w-4 animate-spin" />}
          {getSaveLabel(isSaving, editId)}
        </button>
      </div>
    </>
  );
};

const toCourtSearchData = ({
  state,
  district,
  complex,
  establishment,
  courtRoom,
}: {
  state: string;
  district: string;
  complex: string;
  establishment: string;
  courtRoom: string;
}): SearchData => ({
  cnr: "",
  highCourt: "",
  state,
  highcourt: "",
  bench: "",
  benchRoom: "",
  district,
  courtComplex: complex,
  establishment,
  courtName: courtRoom,
  date: "",
  status: "all",
});

export default DistrictStreamSettingsForm;
